import { QRCode } from '@theme/qr-code-generator';

const TERMINAL_COOKIE = 'isTerminal=true';
const IDLE_MS = 90000;
const OVERLAY_ID = 'terminal-qr-overlay';
const STYLE_ID = 'terminal-qr-styles';

/**
 * @returns {boolean}
 */
function hasTerminalCookie() {
  return document.cookie.split('; ').includes(TERMINAL_COOKIE);
}

/**
 * @returns {boolean}
 */
function isTerminalFromUrl() {
  return new URLSearchParams(window.location.search).get('isTerminal') === 'true';
}

/**
 * @returns {boolean}
 */
export function isTerminalMode() {
  return isTerminalFromUrl() || hasTerminalCookie();
}

function setTerminalCookie() {
  document.cookie = `${TERMINAL_COOKIE}; path=/; max-age=86400; SameSite=Lax`;
}

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    #${OVERLAY_ID} {
      position: fixed;
      inset: 0;
      z-index: 100000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.25rem;
      background: rgb(0 0 0 / 55%);
    }

    #${OVERLAY_ID}[hidden] {
      display: none !important;
    }

    #${OVERLAY_ID} .terminal-qr-overlay__panel {
      position: relative;
      width: min(92vw, 420px);
      background: #fff;
      color: #111;
      padding: 1.75rem 1.5rem 1.5rem;
      text-align: center;
      border-radius: 8px;
      box-shadow: 0 12px 40px rgb(0 0 0 / 25%);
    }

    #${OVERLAY_ID} .terminal-qr-overlay__close {
      position: absolute;
      top: 0.5rem;
      right: 0.75rem;
      border: 0;
      background: transparent;
      font-size: 1.75rem;
      line-height: 1;
      cursor: pointer;
      color: #333;
    }

    #${OVERLAY_ID} .terminal-qr-overlay__title {
      margin: 0 0 0.5rem;
      font-size: 1.35rem;
    }

    #${OVERLAY_ID} .terminal-qr-overlay__text {
      margin: 0 0 1rem;
      font-size: 0.95rem;
      line-height: 1.4;
    }

    #${OVERLAY_ID} .terminal-qr-overlay__code {
      display: flex;
      justify-content: center;
      margin: 0 auto 1rem;
      min-height: 240px;
    }

    #${OVERLAY_ID} .terminal-qr-overlay__code img,
    #${OVERLAY_ID} .terminal-qr-overlay__code canvas,
    #${OVERLAY_ID} .terminal-qr-overlay__code table {
      margin: 0 auto;
    }

    #${OVERLAY_ID} .terminal-qr-overlay__status {
      color: #b00020;
      margin: 0 0 1rem;
    }

    #${OVERLAY_ID} .terminal-qr-overlay__link {
      color: #111;
      font-size: 0.85rem;
    }
  `;
  document.head.appendChild(style);
}

/**
 * Persist isTerminal on the cart so checkout / apps can read it.
 * @returns {Promise<object|null>}
 */
async function ensureTerminalCartAttribute() {
  const cartUrl = (window.routes?.cart_url || '/cart').replace(/\/$/, '');
  const updateUrl = `${cartUrl}/update.js`;

  try {
    const response = await fetch(updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { isTerminal: 'true' },
      }),
    });

    if (!response.ok) {
      console.error('Failed to set isTerminal cart attribute:', response.status);
      return null;
    }

    return response.json();
  } catch (error) {
    console.error('Error updating cart attributes:', error);
    return null;
  }
}

/**
 * Build a shareable cart permalink that opens checkout on another device.
 * @param {object} cart
 * @returns {string}
 */
function buildCheckoutPermalink(cart) {
  const items = (cart?.items || []).filter((item) => item.quantity > 0);

  if (!items.length) {
    throw new Error('Cart is empty');
  }

  const path = items.map((item) => `${item.variant_id}:${item.quantity}`).join(',');
  const origin = window.shopUrl || window.location.origin;
  const cartBase = (window.routes?.cart_url || '/cart').replace(/\/$/, '');
  const url = new URL(`${origin}${cartBase}/${path}`);

  url.searchParams.set('checkout', '');
  url.searchParams.set('attributes[isTerminal]', 'true');

  const sellingPlanIds = [
    ...new Set(
      items
        .map((item) => item.selling_plan_allocation?.selling_plan?.id)
        .filter(Boolean)
        .map(String)
    ),
  ];

  if (sellingPlanIds.length === 1) {
    url.searchParams.set('selling_plan', sellingPlanIds[0]);
  }

  return url.toString().replace(/([?&])checkout=(?=&|$)/, '$1checkout');
}

/**
 * Use a plain overlay (not <dialog showModal>) so we do not fight the cart
 * drawer's own <dialog> / top-layer / squeeze behavior.
 * @returns {HTMLElement}
 */
function getOrCreateOverlay() {
  ensureStyles();

  let overlay = document.getElementById(OVERLAY_ID);
  if (overlay) return overlay;

  overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.hidden = true;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Scan to checkout');
  overlay.innerHTML = `
    <div class="terminal-qr-overlay__panel">
      <button type="button" class="terminal-qr-overlay__close" aria-label="Close">&times;</button>
      <h2 class="terminal-qr-overlay__title">Scan to checkout</h2>
      <p class="terminal-qr-overlay__text">Use your phone camera to scan this QR code and complete payment.</p>
      <div class="terminal-qr-overlay__code" data-terminal-qr-code aria-hidden="true"></div>
      <p class="terminal-qr-overlay__status" data-terminal-qr-status hidden></p>
      <a class="terminal-qr-overlay__link" data-terminal-qr-link href="#" target="_blank" rel="noopener">Open checkout link</a>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => {
    overlay.hidden = true;
  };

  overlay.querySelector('.terminal-qr-overlay__close')?.addEventListener('click', close);
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) close();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !overlay.hidden) close();
  });

  return overlay;
}

/**
 * @param {string} checkoutUrl
 */
function showQrOverlay(checkoutUrl) {
  const overlay = getOrCreateOverlay();
  const codeEl = overlay.querySelector('[data-terminal-qr-code]');
  const linkEl = overlay.querySelector('[data-terminal-qr-link]');
  const statusEl = overlay.querySelector('[data-terminal-qr-status]');

  if (!(codeEl instanceof HTMLElement) || !(linkEl instanceof HTMLAnchorElement)) {
    return;
  }

  codeEl.replaceChildren();
  if (statusEl instanceof HTMLElement) statusEl.hidden = true;

  new QRCode(codeEl, {
    text: checkoutUrl,
    width: 240,
    height: 240,
    colorDark: '#000000',
    colorLight: '#ffffff',
  });

  linkEl.href = checkoutUrl;
  overlay.hidden = false;
}

/**
 * @param {string} message
 */
function showQrError(message) {
  const overlay = getOrCreateOverlay();
  const codeEl = overlay.querySelector('[data-terminal-qr-code]');
  const statusEl = overlay.querySelector('[data-terminal-qr-status]');

  if (codeEl instanceof HTMLElement) codeEl.replaceChildren();
  if (statusEl instanceof HTMLElement) {
    statusEl.hidden = false;
    statusEl.textContent = message;
  }

  overlay.hidden = false;
}

/**
 * @param {Event} event
 */
function handleTerminalCheckout(event) {
  if (!isTerminalMode()) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const checkoutButton = target.closest(
    'button#checkout, button[name="checkout"], input[name="checkout"], button.cart__checkout-button'
  );

  if (!(checkoutButton instanceof HTMLElement)) return;

  if (checkoutButton.closest('shopify-accelerated-checkout, shopify-accelerated-checkout-cart')) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  void (async () => {
    try {
      await ensureTerminalCartAttribute();

      const cartUrl = (window.routes?.cart_url || '/cart').replace(/\/$/, '') + '.js';
      const response = await fetch(cartUrl, { headers: { Accept: 'application/json' } });

      if (!response.ok) {
        throw new Error(`Could not load cart (${response.status})`);
      }

      const cart = await response.json();
      const checkoutUrl = buildCheckoutPermalink(cart);
      showQrOverlay(checkoutUrl);
    } catch (error) {
      console.error('Terminal QR checkout failed:', error);
      showQrError('Unable to create checkout QR. Please try again.');
    }
  })();
}

function startIdleTimer() {
  let idleTimeout;

  const clearCartAndRedirect = () => {
    const clearUrl = (window.routes?.cart_url || '/cart').replace(/\/$/, '') + '/clear.js';
    const redirectUrl = window.dutchieRedirectUrl || `${window.shopUrl || ''}/?isTerminal=true`;

    fetch(clearUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })
      .catch((error) => console.error('Error clearing cart:', error))
      .finally(() => {
        window.location.href = redirectUrl;
      });
  };

  const resetIdleTimer = () => {
    clearTimeout(idleTimeout);
    idleTimeout = setTimeout(clearCartAndRedirect, IDLE_MS);
  };

  ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach((eventType) => {
    document.addEventListener(eventType, resetIdleTimer, { passive: true });
  });

  resetIdleTimer();
}

function initTerminalKiosk() {
  if (isTerminalFromUrl()) {
    setTerminalCookie();
    ensureTerminalCartAttribute();
  }

  if (!isTerminalMode()) return;

  document.body.classList.add('is-terminal-mode');
  window.dutchieRedirectUrl =
    window.dutchieRedirectUrl || `${window.shopUrl || window.location.origin}/?isTerminal=true`;

  document.addEventListener('click', handleTerminalCheckout, true);
  startIdleTimer();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTerminalKiosk);
} else {
  initTerminalKiosk();
}
